/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import type * as Types from './types';
export * from './types';
import * as Urql from 'urql';
export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;
export type AllergyInput = {
  name?: string | null;
};

export type AppointmentStatus =
  | 'ADMITTED'
  | 'CANCELLED'
  | 'CHECKED_IN'
  | 'CHECKED_OUT'
  | 'CHECKING_OUT'
  | 'COMPLETED'
  | 'CONFIRMED'
  | 'DAYCARE'
  | 'INPROGRESS'
  | 'MEDBOARD'
  | 'NOSHOW'
  | 'PENDING'
  | 'PLANNED'
  | 'READY_FOR_DISCHARGE'
  | 'WAITLIST';

export type ClinicalNoteInput = {
  datetime?: string | null;
  deleted?: boolean | null;
  fieldData?: Record<string, any> | null;
  locationId?: string | number | null;
  patientId?: string | number | null;
  text?: string | null;
  title?: string | null;
  visitId?: string | number | null;
};

export type CustomVitalInput = {
  id?: string | number | null;
  name?: string | null;
  value?: string | null;
};

export type EncounterSummaryDocumentType =
  /** Discharge Instructions */
  | 'DISCHARGE_INSTRUCTIONS'
  /** Encounter Summary */
  | 'ENCOUNTER_SUMMARY'
  /** Referral Letter */
  | 'REFERRAL_LETTER';

export type InvoiceSortFieldEnum =
  | 'DATETIME'
  | 'ID'
  | 'INSERTED_AT'
  | 'UPDATED_AT';

export type MedicationStatus =
  | 'ACTIVE'
  | 'ANY'
  /** Indicates that a medication order from a Cubex cabinet was declined. Should only be used when the Cubex Two-Way integration is enabled. */
  | 'APPROVAL_DECLINED'
  | 'COMPLETED'
  | 'DECLINED'
  | 'DELETED'
  | 'DRAFT'
  | 'PENDING'
  /** Indicates a medication ordered from a Cubex cabinet that requires approval. Should only be used when the Cubex Two-Way integration is enabled. */
  | 'PENDING_APPROVAL'
  /** Indicates a medication order that requires approval from a licensed provider. Used for the medication approval workflow. */
  | 'PHARMACY_APPROVAL_PENDING'
  | 'REFILLED'
  /** Indicates a medication returned via a Cubex cabinet. Should only be used when the Cubex Two-Way integration is enabled. */
  | 'RETURNED'
  /** Indicates a medication has been sent to the Pharmacy Whiteboard for fulfillment. */
  | 'SENT_TO_PHARMACY'
  | 'STOPPED'
  /** Indicates a medication imported from an external system that requires manual verification before use. */
  | 'VERIFICATION_NEEDED';

export type PatientDocumentInput = {
  category?: string | null;
  name?: string | null;
  visitId?: string | number | null;
};

export type PatientInput = {
  age?: string | null;
  alerts?: Array<string | null> | null;
  birthDate?: string | null;
  birthDay?: number | null;
  birthMonth?: number | null;
  birthYear?: number | null;
  bloodType?: string | null;
  breed?: string | null;
  color?: string | null;
  deceasedDate?: string | null;
  firstHeatDate?: string | null;
  goalWeight?: number | null;
  hisa?: string | null;
  hisaRegistered?: boolean | null;
  historicalId?: string | null;
  identification?: string | null;
  isActive?: boolean | null;
  isDeceased?: boolean | null;
  isDnr?: boolean | null;
  isEstimatedAge?: boolean | null;
  isEstimatedWeight?: boolean | null;
  isMixed?: boolean | null;
  lastSyncedAt?: string | null;
  microchip?: string | null;
  microchipRegistered?: boolean | null;
  name?: string | null;
  neuterDate?: string | null;
  neutered?: boolean | null;
  notes?: string | null;
  preferredWeightUnit?: string | null;
  privateNotes?: string | null;
  registeredName?: string | null;
  sex?: Sex | null;
  species?: string | null;
  subscriptions?: Array<string | null> | null;
  tagIds?: Array<string | number | null> | null;
  verifiedAt?: string | null;
  vitals?: Array<VitalsInput | null> | null;
};

/** The sex of the patient is listed as Sex */
export type Sex =
  | 'FEMALE'
  | 'MALE'
  | 'UNKNOWN';

export type VitalsInput = {
  attitude?: string | null;
  crt?: string | null;
  customs?: Array<CustomVitalInput | null> | null;
  datetime?: string | null;
  encounterId?: string | number | null;
  heartRate?: number | null;
  id?: string | number | null;
  mm?: string | null;
  pain?: number | null;
  patientId?: string | number | null;
  respRate?: number | null;
  temp?: number | null;
  weight?: number | null;
  weightUnit?: WeightUnit | null;
};

export type WeightUnit =
  | 'G'
  | 'KG'
  | 'LB'
  | 'M2';

export type AddSecondaryOwnerMutationVariables = Exact<{
  id: string | number;
  patientId: string | number;
  email?: string | null;
  name?: string | null;
  phoneNumber?: string | null;
  relationshipId: string | number;
  notes?: string | null;
  percentageOwnership: number;
}>;


export type AddSecondaryOwnerMutation = { addCoowner: { id: string | null, phoneNumber: string | null, relationship: string | null, relationshipId: string | null, coownerClient: { id: string, familyName: string | null, givenName: string | null, email: string | null } | null, patient: { id: string, name: string | null } | null } | null };

export type CreateAllergyMutationVariables = Exact<{
  patientId: string | number;
}>;


export type CreateAllergyMutation = { createAllergy: { id: string | null } | null };

export type CreateAppointmentMutationVariables = Exact<{
  providerId: string | number;
  note?: string | null;
  patientId: string | number;
  start: string;
  reason?: string | null;
  duration: number;
  locationId: string | number;
  appointmentTypeId?: string | number | null;
}>;


export type CreateAppointmentMutation = { createAppointment: { id: string, checkInLink: string | null } | null };

export type CreateClientMutationVariables = Exact<{
  givenName: string;
  familyName: string;
  phoneNumber: string;
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  state: string;
  postalCode: string;
  email: string;
  primaryLocationId: string | number;
  isPrimary?: boolean | null;
}>;


export type CreateClientMutation = { createClient: { id: string } };

export type CreateClinicalNoteMutationVariables = Exact<{
  patientId: string | number;
  title?: string | null;
  text: string;
  fieldData?: Record<string, any> | null;
}>;


export type CreateClinicalNoteMutation = { createClinicalNote: { id: string | null } };

export type ClientConsentLinkMutationVariables = Exact<{
  consentId?: string | number | null;
  clientId?: string | number | null;
  patientId: string | number;
  locationId: string | number;
  estimateId?: string | number | null;
  encounterId?: string | number | null;
  consent?: string | null;
  name?: string | null;
}>;


export type ClientConsentLinkMutation = { clientConsentLink: string | null };

export type CreatePatientMutationVariables = Exact<{
  clientId: string | number;
  species: string;
  name: string;
  sex: Types.Sex;
  neutered?: boolean | null;
  birthDate: string;
  isEstimatedAge: boolean;
  breed: string;
  color: string;
  microchip?: string | null;
  microchipRegistered?: boolean | null;
}>;


export type CreatePatientMutation = { createPatient: { age: string | null, birthDate: string | null, breed: string | null, color: string | null, id: string, isActive: boolean | null, isEstimatedAge: boolean | null, name: string | null, neutered: boolean | null, sex: Types.Sex | null, species: string | null, microchip: string | null, microchipRegistered: boolean | null, encounters: Array<{ id: string, name: string | null, nextVisitDate: string | null } | null> | null, immunizations: Array<{ dueDate: string | null, name: string | null }> | null, latestWeight: { unit: Types.WeightUnit | null, value: number | null } | null } };

export type DeleteAllergyMutationVariables = Exact<{
  id: string | number;
}>;


export type DeleteAllergyMutation = { deleteAllergy: { id: string | null } };

export type DeleteAppointmentMutationVariables = Exact<{
  id: string | number;
}>;


export type DeleteAppointmentMutation = { deleteAppointment: { id: string } | null };

export type DeleteClientConsentMutationVariables = Exact<{
  id: string | number;
}>;


export type DeleteClientConsentMutation = { deleteClientConsent: { __typename: 'ClientConsent', id: string | null } | null };

export type DeletePatientDocumentMutationVariables = Exact<{
  id: string | number;
}>;


export type DeletePatientDocumentMutation = { deletePatientDocument: { name: string | null, id: string | null } | null };

export type DeleteSecondaryOwnerMutationVariables = Exact<{
  id: string | number;
}>;


export type DeleteSecondaryOwnerMutation = { deleteCoowner: { name: string | null, id: string | null } | null };

export type EmailImmunizationsRecordMutationVariables = Exact<{
  ids?: Array<string | number> | string | number | null;
  patientId: string | number;
  emails?: Array<string> | string | null;
  locationId?: string | number | null;
}>;


export type EmailImmunizationsRecordMutation = { emailImmunizationsRecord: string | null };

export type EmailMedicalRecordsSummaryMutationVariables = Exact<{
  patientId: string | number;
  locationId?: string | number | null;
  onlyIncludeItems?: Array<string | null> | string | null;
  emailTo: string;
  subject?: string | null;
  body?: string | null;
  isShareMode?: boolean | null;
}>;


export type EmailMedicalRecordsSummaryMutation = { emailMedicalRecordsSummary: string | null };

export type RecordPatientWeightMutationVariables = Exact<{
  patientId: string | number;
  weight: number;
  weightUnit: Types.WeightUnit;
}>;


export type RecordPatientWeightMutation = { recordPatientWeight: { id: string | null } | null };

export type UpdateAllergyMutationVariables = Exact<{
  id: string | number;
  input: Types.AllergyInput;
}>;


export type UpdateAllergyMutation = { updateAllergy: { id: string | null, name: string | null } | null };

export type UpdateAppointmentMutationVariables = Exact<{
  id: string | number;
  start: string;
  locationId: string | number;
  providerId?: string | number | null;
}>;


export type UpdateAppointmentMutation = { updateAppointment: { id: string, start: string | null, location: { id: string, name: string, displayName: string | null, phoneNumber: string | null, timezone: string | null, latitude: string | null, longitude: string | null, addressFields: { city: string | null, state: string | null, line1: string | null, line2: string | null, postalCode: string | null } | null, attributes: Array<{ value: string | null, customAttribute: { id: string | null, name: string | null } | null } | null> | null } | null } | null };

export type UpdateClientExternalCommPreferencesMutationVariables = Exact<{
  clientId: string | number;
  smsTransactional?: boolean | null;
  smsMarketing?: boolean | null;
  emailMarketing?: boolean | null;
  emailTransactional?: boolean | null;
}>;


export type UpdateClientExternalCommPreferencesMutation = { updateClientExternalCommPreferences: { id: string | null, clientId: string | null, smsTransactional: boolean | null, smsMarketing: boolean | null, emailMarketing: boolean | null, emailTransactional: boolean | null } | null };

export type UpdateClinicalNoteMutationVariables = Exact<{
  id: string | number;
  input: Types.ClinicalNoteInput;
}>;


export type UpdateClinicalNoteMutation = { updateClinicalNote: { id: string | null, text: string | null } | null };

export type UpdatePatientMutationVariables = Exact<{
  patientId: string | number;
  species?: string | null;
  name?: string | null;
  sex?: Types.Sex | null;
  neutered?: boolean | null;
  birthDate?: string | null;
  isEstimatedAge?: boolean | null;
  breed?: string | null;
  color?: string | null;
  microchip?: string | null;
  microchipRegistered?: boolean | null;
  isDeceased?: boolean | null;
}>;


export type UpdatePatientMutation = { updatePatient: { id: string, age: string | null } | null };

export type UpdatePatientAlertsMutationVariables = Exact<{
  id: string | number;
  input: Types.PatientInput;
}>;


export type UpdatePatientAlertsMutation = { updatePatient: { id: string, alerts: Array<string> | null } | null };

export type UpdatePatientDocumentMutationVariables = Exact<{
  id: string | number;
  input: Types.PatientDocumentInput;
}>;


export type UpdatePatientDocumentMutation = { updatePatientDocument: { id: string | null, name: string | null, category: string | null } | null };

export type UpdateSecondaryOwnerMutationVariables = Exact<{
  id: string | number;
  email?: string | null;
  name?: string | null;
  phoneNumber?: string | null;
  relationshipId: string | number;
  notes?: string | null;
  percentageOwnership?: number | null;
  patientId?: string | number | null;
}>;


export type UpdateSecondaryOwnerMutation = { updateCoowner: { id: string | null, phoneNumber: string | null, relationship: string | null, relationshipId: string | null, coownerClient: { id: string, familyName: string | null, givenName: string | null, email: string | null } | null, patient: { id: string, name: string | null } | null } | null };

export type UpdateClientMutationVariables = Exact<{
  clientId: string | number;
  givenName?: string | null;
  familyName?: string | null;
  phoneNumber?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  email?: string | null;
  primaryLocationId?: string | number | null;
  isPrimary?: boolean | null;
}>;


export type UpdateClientMutation = { updateClient: { id: string } | null };

export type ImmunizationPdfQueryVariables = Exact<{
  patientId: string | number;
  locationId: string | number;
  ids?: Array<string | number | null> | string | number | null;
}>;


export type ImmunizationPdfQuery = { immunizationPdf: string | null };

export type AppointmentTypesQueryVariables = Exact<{ [key: string]: never; }>;


export type AppointmentTypesQuery = { appointmentTypes: Array<{ id: string, name: string | null, duration: number | null }> };

export type AvailableSlotsByLocationsQueryVariables = Exact<{
  ids?: Array<number | null> | number | null;
  appointmentTypeId: string | number;
  startDate?: string | null;
  endDate?: string | null;
  date?: string | null;
}>;


export type AvailableSlotsByLocationsQuery = { locations: Array<{ id: string, appointmentSlotMinutes: number | null, availableSlots: Array<{ date: string | null, id: string | null, providerId: string | null, scheduleId: string | null, time: string | null, provider: { username: string | null } | null } | null> | null }> | null };

export type CareEstimatePdfQueryVariables = Exact<{
  estimateId: string | number;
}>;


export type CareEstimatePdfQuery = { estimatePdf: string | null };

export type CareEstimatesQueryVariables = Exact<{
  clientId: string | number;
  closed?: boolean | null;
  updatedAtStart?: string | null;
  updatedAtEnd?: string | null;
}>;


export type CareEstimatesQuery = { estimates: Array<{ id: string, datetime: string | null, totalPaid: number | null, updatedAt: string | null, patients: Array<{ id: string, name: string | null } | null> | null } | null> | null };

export type ClientByEmailQueryVariables = Exact<{
  email: string;
}>;


export type ClientByEmailQuery = { clients: Array<ClientFieldsFragment | null> | null };

export type ClientFieldsFragment = { id: string, givenName: string | null, familyName: string | null, email: string | null, isActive: boolean | null, primaryLocationId: string | null, phoneNumbers: Array<{ value: string | null } | null> | null, addresses: Array<{ postalCode: string | null, line1: string | null, line2: string | null, city: string | null, state: string | null } | null> | null, patients: Array<{ id: string, name: string | null, age: string | null, species: string | null, isActive: boolean | null, isDeceased: boolean | null, birthDate: string | null, isEstimatedAge: boolean | null, breed: string | null, sex: Types.Sex | null, neutered: boolean | null, color: string | null, microchip: string | null, microchipRegistered: boolean | null, profileImageUrl: string | null, insertedAt: string | null, latestWeight: { unit: Types.WeightUnit | null, value: number | null } | null, immunizations: Array<{ name: string | null, dueDate: string | null }> | null, encounters: Array<{ id: string, name: string | null, nextVisitDate: string | null } | null> | null }> | null, externalCommPreferences: Array<{ smsMarketing: boolean | null, smsTransactional: boolean | null, emailMarketing: boolean | null, emailTransactional: boolean | null } | null> | null, tags: Array<{ name: string | null, id: string }> };

export type ClientByIdQueryVariables = Exact<{
  id: string | number;
}>;


export type ClientByIdQuery = { client: ClientFieldsFragment | null };

export type ClientEncountersQueryVariables = Exact<{
  clientId: string | number;
}>;


export type ClientEncountersQuery = { encounters: Array<{ category: string | null, id: string, appointment: { id: string, start: string | null, type: { description: string | null, id: string, name: string | null } | null } | null, encounterType: { id: string | null, name: string | null, sections: Array<{ name: string | null, isPrivate: boolean | null } | null> | null } | null, patient: { id: string, name: string | null } | null } | null> | null };

export type ClientsQueryVariables = Exact<{
  familyName: string;
  phoneNumber: string;
}>;


export type ClientsQuery = { clients: Array<{ id: string } | null> | null };

export type GetConsentFormsQueryVariables = Exact<{ [key: string]: never; }>;


export type GetConsentFormsQuery = { consentForms: Array<{ id: string | null, name: string | null } | null> | null };

export type ConsentFormsQueryVariables = Exact<{
  patientId?: string | number | null;
  clientId: string | number;
}>;


export type ConsentFormsQuery = { patientConsentForms: Array<{ id: string | null, name: string | null, consent: string | null, signed: boolean | null, signedDatetime: string | null, signature: string | null, patient: { id: string, name: string | null } | null } | null> | null };

export type EncounterQueryVariables = Exact<{
  encounterId: string | number;
  documentType?: Types.EncounterSummaryDocumentType | null;
  extras?: Array<string | null> | string | null;
  onlySections?: Array<string | null> | string | null;
}>;


export type EncounterQuery = { encounterSummary: string | null };

export type GetConsentFormQueryVariables = Exact<{
  consentFormId: string | number;
  locationId: string | number;
}>;


export type GetConsentFormQuery = { consentForm: { id: string | null, name: string | null, body: string | null } | null, location: { id: string, logoUri: string | null, name: string, phoneNumber: string | null } | null };

export type HistoricalWeightsQueryVariables = Exact<{
  patientId: string | number;
}>;


export type HistoricalWeightsQuery = { vitals: Array<{ id: string | null, weight: number | null, weightUnit: Types.WeightUnit | null, insertedAt: string | null, datetime: string | null, encounter: { id: string, start: string | null } | null } | null> | null };

export type ImmunizationsQueryVariables = Exact<{
  clientId: string | number;
  patientId?: string | number | null;
  ignoreHistorical?: boolean | null;
  isLatest?: boolean | null;
  isRabies?: boolean | null;
  limit?: number | null;
  offset?: number | null;
}>;


export type ImmunizationsQuery = { immunizations: Array<{ id: string | null, dueDate: string | null, date: string | null, administered: boolean | null, updatedAt: string | null, isRabies: boolean | null, name: string | null, location: { id: string } | null, patient: { id: string, name: string | null } | null } | null> | null };

export type InvoicePdfQueryVariables = Exact<{
  invoiceId: string | number;
  coownerClientId?: string | number | null;
}>;


export type InvoicePdfQuery = { invoicePdf: string | null };

export type InvoicesQueryVariables = Exact<{
  clientId: string | number;
  invoiced?: boolean | null;
  posted?: boolean | null;
  orderBy?: Types.InvoiceSortFieldEnum | null;
  start?: string | null;
}>;


export type InvoicesQuery = { orders: Array<{ id: string | null, datetime: string | null, totalPaid: number | null, updatedAt: string | null, patients: Array<{ name: string | null, id: string } | null> | null } | null> | null };

export type LabOrderResultPdfQueryVariables = Exact<{
  documentId: string | number;
}>;


export type LabOrderResultPdfQuery = { patientDocument: { name: string | null, media: string | null, contentType: string | null } | null };

export type LocationQueryVariables = Exact<{
  id: string | number;
}>;


export type LocationQuery = { location: { addressString: string | null, displayName: string | null, id: string, latitude: string | null, longitude: string | null, phoneNumber: string | null, timezone: string | null, addressFields: { city: string | null, line1: string | null, line2: string | null, postalCode: string | null, state: string | null, text: string | null } | null, attributes: Array<{ value: string | null, customAttribute: { id: string | null, name: string | null } | null } | null> | null, locationHours: { mondayRanges: Array<string | null> | null, tuesdayRanges: Array<string | null> | null, wednesdayRanges: Array<string | null> | null, thursdayRanges: Array<string | null> | null, fridayRanges: Array<string | null> | null, saturdayRanges: Array<string | null> | null, sundayRanges: Array<string | null> | null } | null } | null };

export type LocationsQueryVariables = Exact<{ [key: string]: never; }>;


export type LocationsQuery = { locations: Array<{ name: string, addressString: string | null, displayName: string | null, id: string, latitude: string | null, longitude: string | null, phoneNumber: string | null, timezone: string | null, addressFields: { city: string | null, line1: string | null, line2: string | null, postalCode: string | null, state: string | null, text: string | null } | null, attributes: Array<{ value: string | null, customAttribute: { id: string | null, name: string | null } | null } | null> | null, locationHours: { mondayRanges: Array<string | null> | null, tuesdayRanges: Array<string | null> | null, wednesdayRanges: Array<string | null> | null, thursdayRanges: Array<string | null> | null, fridayRanges: Array<string | null> | null, saturdayRanges: Array<string | null> | null, sundayRanges: Array<string | null> | null } | null }> | null };

export type MedicationsQueryVariables = Exact<{
  patientId?: string | number | null;
}>;


export type MedicationsQuery = { medications: Array<{ id: string, name: string | null, daysSupply: number | null, quantity: number | null, quantityUnit: string | null, refillByDate: string | null, refills: number | null, refillsUsed: number | null, start: string | null, status: Types.MedicationStatus | null, stop: string | null, sig: string | null, note: string | null, declined: boolean | null, dispensed: number | null, dose: string | null, frequency: string | null, historical: boolean | null, insertedAt: string | null, updatedAt: string | null, prescriber: { name: string | null } | null, location: { timezone: string | null } | null } | null> | null };

export type OrgQueryVariables = Exact<{ [key: string]: never; }>;


export type OrgQuery = { org: { id: string | null, name: string | null } | null };

export type PatientDetailsQueryVariables = Exact<{
  patientId: string | number;
}>;


export type PatientDetailsQuery = { patient: { id: string, alerts: Array<string> | null, allergies: Array<{ id: string | null, name: string | null } | null> | null, clinicalNotes: Array<{ id: string | null, text: string | null, deleted: boolean | null } | null> | null } | null };

export type PatientDocumentsQueryVariables = Exact<{
  patientId: string | number;
}>;


export type PatientDocumentsQuery = { patient: { documents: Array<{ id: string | null, insertedAt: string | null, category: string | null, datetime: string | null, name: string | null, media: string | null, uploader: { id: string, name: string | null, providerRole: { name: string | null } | null } | null, patient: { name: string | null, id: string } | null, location: { id: string } | null } | null> | null, clinicalNotes: Array<{ id: string | null, title: string | null, fieldData: Record<string, any> | null, text: string | null } | null> | null } | null };

export type PreviousAppointmentsQueryVariables = Exact<{
  clientId: string | number;
  patientId?: string | number | null;
  start?: string | null;
  end?: string | null;
  limit?: number | null;
  offset?: number | null;
}>;


export type PreviousAppointmentsQuery = { appointments: Array<{ id: string, start: string | null, patient: { name: string | null, species: string | null } | null, location: { name: string, timezone: string | null, addressFields: { city: string | null, state: string | null } | null } | null, type: { name: string | null } | null, encounters: Array<{ id: string, sharedAt: string | null, sectionsJson: string | null, encounterType: { id: string | null, name: string | null, sections: Array<{ isPrivate: boolean | null, name: string | null } | null> | null } | null } | null> | null } | null> | null };

export type RelationshipsQueryVariables = Exact<{ [key: string]: never; }>;


export type RelationshipsQuery = { relationships: Array<{ id: string | null, name: string | null } | null> | null };

export type UserInformationQueryVariables = Exact<{
  id: string | number;
}>;


export type UserInformationQuery = { client: { id: string, coowners: Array<{ id: string | null, email: string | null, phoneNumber: string | null, name: string | null, relationship: string | null, relationshipId: string | null, notes: string | null, coownerClient: { id: string, familyName: string | null, givenName: string | null, email: string | null } | null, patient: { id: string, name: string | null } | null } | null> | null } | null, relationships: Array<{ id: string | null, name: string | null } | null> | null };

export type VerifyUserAppointmentsQueryVariables = Exact<{
  clientId: string | number;
  start?: string | null;
}>;


export type VerifyUserAppointmentsQuery = { appointments: Array<{ id: string, status: Types.AppointmentStatus | null, reason: string | null, start: string | null, note: string | null, type: { id: string, name: string | null } | null, provider: { id: string } | null, location: { id: string, name: string, displayName: string | null, phoneNumber: string | null, latitude: string | null, longitude: string | null, timezone: string | null, addressFields: { city: string | null, state: string | null, line1: string | null, line2: string | null, postalCode: string | null } | null, attributes: Array<{ value: string | null, customAttribute: { id: string | null, name: string | null } | null } | null> | null } | null, patient: { id: string, name: string | null, species: string | null } | null } | null> | null };

export const ClientFieldsFragmentDoc = `
    fragment ClientFields on Client {
  id
  givenName
  familyName
  email
  phoneNumbers {
    value
  }
  isActive
  addresses {
    postalCode
    line1
    line2
    city
    state
  }
  primaryLocationId
  patients {
    id
    name
    age
    species
    isActive
    isDeceased
    birthDate
    isEstimatedAge
    latestWeight {
      unit
      value
    }
    breed
    sex
    neutered
    color
    immunizations {
      name
      dueDate
    }
    encounters {
      id
      name
      nextVisitDate
    }
    microchip
    microchipRegistered
    profileImageUrl
    insertedAt
  }
  externalCommPreferences {
    smsMarketing
    smsTransactional
    emailMarketing
    emailTransactional
  }
  tags {
    name
    id
  }
}
    `;
export const AddSecondaryOwnerDocument = `
    mutation AddSecondaryOwner($id: ID!, $patientId: ID!, $email: String, $name: String, $phoneNumber: String, $relationshipId: ID!, $notes: String, $percentageOwnership: Decimal!) {
  addCoowner(
    clientId: $id
    input: {percentageOwnership: $percentageOwnership, patientId: $patientId, email: $email, name: $name, phoneNumber: $phoneNumber, relationshipId: $relationshipId, notes: $notes}
  ) {
    id
    coownerClient {
      id
      familyName
      givenName
      email
    }
    patient {
      id
      name
    }
    phoneNumber
    relationship
    relationshipId
  }
}
    `;

export function useAddSecondaryOwnerMutation() {
  return Urql.useMutation<AddSecondaryOwnerMutation, AddSecondaryOwnerMutationVariables>(AddSecondaryOwnerDocument);
};
export const CreateAllergyDocument = `
    mutation createAllergy($patientId: ID!) {
  createAllergy(patientId: $patientId) {
    id
  }
}
    `;

export function useCreateAllergyMutation() {
  return Urql.useMutation<CreateAllergyMutation, CreateAllergyMutationVariables>(CreateAllergyDocument);
};
export const CreateAppointmentDocument = `
    mutation CreateAppointment($providerId: ID!, $note: String, $patientId: ID!, $start: DateTime!, $reason: String, $duration: Int!, $locationId: ID!, $appointmentTypeId: ID) {
  createAppointment(
    input: {providerId: $providerId, note: $note, patientId: $patientId, start: $start, reason: $reason, duration: $duration, bookedOnline: true, locationId: $locationId, appointmentTypeId: $appointmentTypeId}
  ) {
    id
    checkInLink
  }
}
    `;

export function useCreateAppointmentMutation() {
  return Urql.useMutation<CreateAppointmentMutation, CreateAppointmentMutationVariables>(CreateAppointmentDocument);
};
export const CreateClientDocument = `
    mutation CreateClient($givenName: String!, $familyName: String!, $phoneNumber: String!, $addressLine1: String!, $addressLine2: String, $city: String!, $state: String!, $postalCode: String!, $email: String!, $primaryLocationId: ID!, $isPrimary: Boolean = true) {
  createClient(
    input: {givenName: $givenName, familyName: $familyName, email: $email, addresses: [{line1: $addressLine1, line2: $addressLine2, state: $state, city: $city, postalCode: $postalCode, isPrimary: $isPrimary}], phoneNumbers: [{value: $phoneNumber, preferred: true}], primaryLocationId: $primaryLocationId}
  ) {
    id
  }
}
    `;

export function useCreateClientMutation() {
  return Urql.useMutation<CreateClientMutation, CreateClientMutationVariables>(CreateClientDocument);
};
export const CreateClinicalNoteDocument = `
    mutation CreateClinicalNote($patientId: ID!, $title: String, $text: String!, $fieldData: JSON) {
  createClinicalNote(
    input: {patientId: $patientId, title: $title, text: $text, fieldData: $fieldData}
  ) {
    id
  }
}
    `;

export function useCreateClinicalNoteMutation() {
  return Urql.useMutation<CreateClinicalNoteMutation, CreateClinicalNoteMutationVariables>(CreateClinicalNoteDocument);
};
export const ClientConsentLinkDocument = `
    mutation clientConsentLink($consentId: ID, $clientId: ID, $patientId: ID!, $locationId: ID!, $estimateId: ID, $encounterId: ID, $consent: String, $name: String) {
  clientConsentLink(
    consentFormId: $consentId
    clientId: $clientId
    patientId: $patientId
    locationId: $locationId
    estimateId: $estimateId
    encounterId: $encounterId
    consent: $consent
    name: $name
  )
}
    `;

export function useClientConsentLinkMutation() {
  return Urql.useMutation<ClientConsentLinkMutation, ClientConsentLinkMutationVariables>(ClientConsentLinkDocument);
};
export const CreatePatientDocument = `
    mutation CreatePatient($clientId: ID!, $species: String!, $name: String!, $sex: Sex!, $neutered: Boolean, $birthDate: Date!, $isEstimatedAge: Boolean!, $breed: String!, $color: String!, $microchip: String, $microchipRegistered: Boolean) {
  createPatient(
    clientId: $clientId
    input: {species: $species, name: $name, sex: $sex, neutered: $neutered, birthDate: $birthDate, isEstimatedAge: $isEstimatedAge, breed: $breed, color: $color, microchip: $microchip, microchipRegistered: $microchipRegistered}
  ) {
    age
    birthDate
    breed
    color
    encounters {
      id
      name
      nextVisitDate
    }
    id
    immunizations {
      dueDate
      name
    }
    isActive
    isEstimatedAge
    latestWeight {
      unit
      value
    }
    name
    neutered
    sex
    species
    microchip
    microchipRegistered
  }
}
    `;

export function useCreatePatientMutation() {
  return Urql.useMutation<CreatePatientMutation, CreatePatientMutationVariables>(CreatePatientDocument);
};
export const DeleteAllergyDocument = `
    mutation deleteAllergy($id: ID!) {
  deleteAllergy(id: $id) {
    id
  }
}
    `;

export function useDeleteAllergyMutation() {
  return Urql.useMutation<DeleteAllergyMutation, DeleteAllergyMutationVariables>(DeleteAllergyDocument);
};
export const DeleteAppointmentDocument = `
    mutation DeleteAppointment($id: ID!) {
  deleteAppointment(id: $id) {
    id
  }
}
    `;

export function useDeleteAppointmentMutation() {
  return Urql.useMutation<DeleteAppointmentMutation, DeleteAppointmentMutationVariables>(DeleteAppointmentDocument);
};
export const DeleteClientConsentDocument = `
    mutation deleteClientConsent($id: ID!) {
  deleteClientConsent(id: $id) {
    id
    __typename
  }
}
    `;

export function useDeleteClientConsentMutation() {
  return Urql.useMutation<DeleteClientConsentMutation, DeleteClientConsentMutationVariables>(DeleteClientConsentDocument);
};
export const DeletePatientDocumentDocument = `
    mutation deletePatientDocument($id: ID!) {
  deletePatientDocument(id: $id) {
    name
    id
  }
}
    `;

export function useDeletePatientDocumentMutation() {
  return Urql.useMutation<DeletePatientDocumentMutation, DeletePatientDocumentMutationVariables>(DeletePatientDocumentDocument);
};
export const DeleteSecondaryOwnerDocument = `
    mutation DeleteSecondaryOwner($id: ID!) {
  deleteCoowner(id: $id) {
    name
    id
  }
}
    `;

export function useDeleteSecondaryOwnerMutation() {
  return Urql.useMutation<DeleteSecondaryOwnerMutation, DeleteSecondaryOwnerMutationVariables>(DeleteSecondaryOwnerDocument);
};
export const EmailImmunizationsRecordDocument = `
    mutation EmailImmunizationsRecord($ids: [ID!], $patientId: ID!, $emails: [String!], $locationId: ID) {
  emailImmunizationsRecord(
    ids: $ids
    patientId: $patientId
    emails: $emails
    locationId: $locationId
    isLatest: false
  )
}
    `;

export function useEmailImmunizationsRecordMutation() {
  return Urql.useMutation<EmailImmunizationsRecordMutation, EmailImmunizationsRecordMutationVariables>(EmailImmunizationsRecordDocument);
};
export const EmailMedicalRecordsSummaryDocument = `
    mutation EmailMedicalRecordsSummary($patientId: ID!, $locationId: ID, $onlyIncludeItems: [String], $emailTo: String!, $subject: String, $body: String, $isShareMode: Boolean) {
  emailMedicalRecordsSummary(
    patientId: $patientId
    locationId: $locationId
    onlyIncludeItems: $onlyIncludeItems
    emailTo: $emailTo
    subject: $subject
    body: $body
    isShareMode: $isShareMode
  )
}
    `;

export function useEmailMedicalRecordsSummaryMutation() {
  return Urql.useMutation<EmailMedicalRecordsSummaryMutation, EmailMedicalRecordsSummaryMutationVariables>(EmailMedicalRecordsSummaryDocument);
};
export const RecordPatientWeightDocument = `
    mutation RecordPatientWeight($patientId: ID!, $weight: Decimal!, $weightUnit: WeightUnit!) {
  recordPatientWeight(
    patientId: $patientId
    weight: $weight
    weightUnit: $weightUnit
  ) {
    id
  }
}
    `;

export function useRecordPatientWeightMutation() {
  return Urql.useMutation<RecordPatientWeightMutation, RecordPatientWeightMutationVariables>(RecordPatientWeightDocument);
};
export const UpdateAllergyDocument = `
    mutation updateAllergy($id: ID!, $input: AllergyInput!) {
  updateAllergy(id: $id, input: $input) {
    id
    name
  }
}
    `;

export function useUpdateAllergyMutation() {
  return Urql.useMutation<UpdateAllergyMutation, UpdateAllergyMutationVariables>(UpdateAllergyDocument);
};
export const UpdateAppointmentDocument = `
    mutation UpdateAppointment($id: ID!, $start: DateTime!, $locationId: ID!, $providerId: ID) {
  updateAppointment(
    id: $id
    input: {start: $start, locationId: $locationId, providerId: $providerId}
  ) {
    id
    start
    location {
      id
      name
      displayName
      addressFields {
        city
        state
        line1
        line2
        postalCode
      }
      phoneNumber
      timezone
      attributes {
        customAttribute {
          id
          name
        }
        value
      }
      latitude
      longitude
    }
  }
}
    `;

export function useUpdateAppointmentMutation() {
  return Urql.useMutation<UpdateAppointmentMutation, UpdateAppointmentMutationVariables>(UpdateAppointmentDocument);
};
export const UpdateClientExternalCommPreferencesDocument = `
    mutation updateClientExternalCommPreferences($clientId: ID!, $smsTransactional: Boolean, $smsMarketing: Boolean, $emailMarketing: Boolean, $emailTransactional: Boolean) {
  updateClientExternalCommPreferences(
    clientId: $clientId
    input: {smsTransactional: $smsTransactional, smsMarketing: $smsMarketing, emailMarketing: $emailMarketing, emailTransactional: $emailTransactional}
  ) {
    id
    clientId
    smsTransactional
    smsMarketing
    emailMarketing
    emailTransactional
  }
}
    `;

export function useUpdateClientExternalCommPreferencesMutation() {
  return Urql.useMutation<UpdateClientExternalCommPreferencesMutation, UpdateClientExternalCommPreferencesMutationVariables>(UpdateClientExternalCommPreferencesDocument);
};
export const UpdateClinicalNoteDocument = `
    mutation UpdateClinicalNote($id: ID!, $input: ClinicalNoteInput!) {
  updateClinicalNote(id: $id, input: $input) {
    id
    text
  }
}
    `;

export function useUpdateClinicalNoteMutation() {
  return Urql.useMutation<UpdateClinicalNoteMutation, UpdateClinicalNoteMutationVariables>(UpdateClinicalNoteDocument);
};
export const UpdatePatientDocument = `
    mutation UpdatePatient($patientId: ID!, $species: String, $name: String, $sex: Sex, $neutered: Boolean, $birthDate: Date, $isEstimatedAge: Boolean, $breed: String, $color: String, $microchip: String, $microchipRegistered: Boolean, $isDeceased: Boolean) {
  updatePatient(
    id: $patientId
    input: {species: $species, name: $name, sex: $sex, neutered: $neutered, birthDate: $birthDate, isEstimatedAge: $isEstimatedAge, breed: $breed, color: $color, microchip: $microchip, microchipRegistered: $microchipRegistered, isDeceased: $isDeceased}
  ) {
    id
    age
  }
}
    `;

export function useUpdatePatientMutation() {
  return Urql.useMutation<UpdatePatientMutation, UpdatePatientMutationVariables>(UpdatePatientDocument);
};
export const UpdatePatientAlertsDocument = `
    mutation updatePatientAlerts($id: ID!, $input: PatientInput!) {
  updatePatient(id: $id, input: $input) {
    id
    alerts
  }
}
    `;

export function useUpdatePatientAlertsMutation() {
  return Urql.useMutation<UpdatePatientAlertsMutation, UpdatePatientAlertsMutationVariables>(UpdatePatientAlertsDocument);
};
export const UpdatePatientDocumentDocument = `
    mutation UpdatePatientDocument($id: ID!, $input: PatientDocumentInput!) {
  updatePatientDocument(id: $id, input: $input) {
    id
    name
    category
  }
}
    `;

export function useUpdatePatientDocumentMutation() {
  return Urql.useMutation<UpdatePatientDocumentMutation, UpdatePatientDocumentMutationVariables>(UpdatePatientDocumentDocument);
};
export const UpdateSecondaryOwnerDocument = `
    mutation UpdateSecondaryOwner($id: ID!, $email: String, $name: String, $phoneNumber: String, $relationshipId: ID!, $notes: String, $percentageOwnership: Decimal, $patientId: ID) {
  updateCoowner(
    id: $id
    input: {percentageOwnership: $percentageOwnership, patientId: $patientId, email: $email, name: $name, phoneNumber: $phoneNumber, relationshipId: $relationshipId, notes: $notes}
  ) {
    id
    coownerClient {
      id
      familyName
      givenName
      email
    }
    patient {
      id
      name
    }
    phoneNumber
    relationship
    relationshipId
  }
}
    `;

export function useUpdateSecondaryOwnerMutation() {
  return Urql.useMutation<UpdateSecondaryOwnerMutation, UpdateSecondaryOwnerMutationVariables>(UpdateSecondaryOwnerDocument);
};
export const UpdateClientDocument = `
    mutation UpdateClient($clientId: ID!, $givenName: String, $familyName: String, $phoneNumber: String, $addressLine1: String, $addressLine2: String, $city: String, $state: String, $postalCode: String, $email: String, $primaryLocationId: ID, $isPrimary: Boolean = true) {
  updateClient(
    id: $clientId
    input: {givenName: $givenName, familyName: $familyName, email: $email, addresses: [{line1: $addressLine1, line2: $addressLine2, city: $city, state: $state, postalCode: $postalCode, isPrimary: $isPrimary}], phoneNumbers: [{value: $phoneNumber, preferred: true}], primaryLocationId: $primaryLocationId}
  ) {
    id
  }
}
    `;

export function useUpdateClientMutation() {
  return Urql.useMutation<UpdateClientMutation, UpdateClientMutationVariables>(UpdateClientDocument);
};
export const ImmunizationPdfDocument = `
    query ImmunizationPdf($patientId: ID!, $locationId: ID!, $ids: [ID]) {
  immunizationPdf(patientId: $patientId, locationId: $locationId, ids: $ids)
}
    `;

export function useImmunizationPdfQuery(options: Omit<Urql.UseQueryArgs<ImmunizationPdfQueryVariables>, 'query'>) {
  return Urql.useQuery<ImmunizationPdfQuery, ImmunizationPdfQueryVariables>({ query: ImmunizationPdfDocument, ...options });
};
export const AppointmentTypesDocument = `
    query AppointmentTypes {
  appointmentTypes {
    id
    name
    duration
  }
}
    `;

export function useAppointmentTypesQuery(options?: Omit<Urql.UseQueryArgs<AppointmentTypesQueryVariables>, 'query'>) {
  return Urql.useQuery<AppointmentTypesQuery, AppointmentTypesQueryVariables>({ query: AppointmentTypesDocument, ...options });
};
export const AvailableSlotsByLocationsDocument = `
    query AvailableSlotsByLocations($ids: [Int], $appointmentTypeId: ID!, $startDate: Date, $endDate: Date, $date: Date) {
  locations(filters: {ids: $ids}) {
    id
    appointmentSlotMinutes
    availableSlots(
      appointmentTypeId: $appointmentTypeId
      startDate: $startDate
      endDate: $endDate
      date: $date
    ) {
      date
      id
      providerId
      scheduleId
      time
      provider {
        username
      }
    }
  }
}
    `;

export function useAvailableSlotsByLocationsQuery(options: Omit<Urql.UseQueryArgs<AvailableSlotsByLocationsQueryVariables>, 'query'>) {
  return Urql.useQuery<AvailableSlotsByLocationsQuery, AvailableSlotsByLocationsQueryVariables>({ query: AvailableSlotsByLocationsDocument, ...options });
};
export const CareEstimatePdfDocument = `
    query CareEstimatePdf($estimateId: ID!) {
  estimatePdf(id: $estimateId)
}
    `;

export function useCareEstimatePdfQuery(options: Omit<Urql.UseQueryArgs<CareEstimatePdfQueryVariables>, 'query'>) {
  return Urql.useQuery<CareEstimatePdfQuery, CareEstimatePdfQueryVariables>({ query: CareEstimatePdfDocument, ...options });
};
export const CareEstimatesDocument = `
    query CareEstimates($clientId: ID!, $closed: Boolean = true, $updatedAtStart: NaiveDateTime, $updatedAtEnd: NaiveDateTime) {
  estimates(
    clientId: $clientId
    closed: $closed
    updatedAtStart: $updatedAtStart
    updatedAtEnd: $updatedAtEnd
  ) {
    id
    datetime
    patients {
      id
      name
    }
    totalPaid
    updatedAt
  }
}
    `;

export function useCareEstimatesQuery(options: Omit<Urql.UseQueryArgs<CareEstimatesQueryVariables>, 'query'>) {
  return Urql.useQuery<CareEstimatesQuery, CareEstimatesQueryVariables>({ query: CareEstimatesDocument, ...options });
};
export const ClientByEmailDocument = `
    query ClientByEmail($email: String!) {
  clients(filters: {email: $email, isActive: true}, limit: 1) {
    ...ClientFields
  }
}
    ${ClientFieldsFragmentDoc}`;

export function useClientByEmailQuery(options: Omit<Urql.UseQueryArgs<ClientByEmailQueryVariables>, 'query'>) {
  return Urql.useQuery<ClientByEmailQuery, ClientByEmailQueryVariables>({ query: ClientByEmailDocument, ...options });
};
export const ClientByIdDocument = `
    query ClientById($id: ID!) {
  client(id: $id) {
    ...ClientFields
  }
}
    ${ClientFieldsFragmentDoc}`;

export function useClientByIdQuery(options: Omit<Urql.UseQueryArgs<ClientByIdQueryVariables>, 'query'>) {
  return Urql.useQuery<ClientByIdQuery, ClientByIdQueryVariables>({ query: ClientByIdDocument, ...options });
};
export const ClientEncountersDocument = `
    query ClientEncounters($clientId: ID!) {
  encounters(clientId: $clientId) {
    appointment {
      type {
        description
        id
        name
      }
      id
      start
    }
    category
    encounterType {
      id
      name
      sections {
        name
        isPrivate
      }
    }
    patient {
      id
      name
    }
    id
  }
}
    `;

export function useClientEncountersQuery(options: Omit<Urql.UseQueryArgs<ClientEncountersQueryVariables>, 'query'>) {
  return Urql.useQuery<ClientEncountersQuery, ClientEncountersQueryVariables>({ query: ClientEncountersDocument, ...options });
};
export const ClientsDocument = `
    query Clients($familyName: String!, $phoneNumber: String!) {
  clients(filters: {familyName: $familyName, phoneNumber: $phoneNumber}) {
    id
  }
}
    `;

export function useClientsQuery(options: Omit<Urql.UseQueryArgs<ClientsQueryVariables>, 'query'>) {
  return Urql.useQuery<ClientsQuery, ClientsQueryVariables>({ query: ClientsDocument, ...options });
};
export const GetConsentFormsDocument = `
    query getConsentForms {
  consentForms {
    id
    name
  }
}
    `;

export function useGetConsentFormsQuery(options?: Omit<Urql.UseQueryArgs<GetConsentFormsQueryVariables>, 'query'>) {
  return Urql.useQuery<GetConsentFormsQuery, GetConsentFormsQueryVariables>({ query: GetConsentFormsDocument, ...options });
};
export const ConsentFormsDocument = `
    query consentForms($patientId: ID, $clientId: ID!) {
  patientConsentForms(patientId: $patientId, clientId: $clientId) {
    id
    name
    consent
    signed
    signedDatetime
    signature
    patient {
      id
      name
    }
  }
}
    `;

export function useConsentFormsQuery(options: Omit<Urql.UseQueryArgs<ConsentFormsQueryVariables>, 'query'>) {
  return Urql.useQuery<ConsentFormsQuery, ConsentFormsQueryVariables>({ query: ConsentFormsDocument, ...options });
};
export const EncounterDocument = `
    query Encounter($encounterId: ID!, $documentType: EncounterSummaryDocumentType, $extras: [String], $onlySections: [String]) {
  encounterSummary(
    encounterId: $encounterId
    documentType: $documentType
    extras: $extras
    onlySections: $onlySections
  )
}
    `;

export function useEncounterQuery(options: Omit<Urql.UseQueryArgs<EncounterQueryVariables>, 'query'>) {
  return Urql.useQuery<EncounterQuery, EncounterQueryVariables>({ query: EncounterDocument, ...options });
};
export const GetConsentFormDocument = `
    query getConsentForm($consentFormId: ID!, $locationId: ID!) {
  consentForm(id: $consentFormId) {
    id
    name
    body
  }
  location(id: $locationId) {
    id
    logoUri
    name
    phoneNumber
  }
}
    `;

export function useGetConsentFormQuery(options: Omit<Urql.UseQueryArgs<GetConsentFormQueryVariables>, 'query'>) {
  return Urql.useQuery<GetConsentFormQuery, GetConsentFormQueryVariables>({ query: GetConsentFormDocument, ...options });
};
export const HistoricalWeightsDocument = `
    query HistoricalWeights($patientId: ID!) {
  vitals(patientId: $patientId) {
    id
    weight
    weightUnit
    encounter {
      id
      start
    }
    insertedAt
    datetime
  }
}
    `;

export function useHistoricalWeightsQuery(options: Omit<Urql.UseQueryArgs<HistoricalWeightsQueryVariables>, 'query'>) {
  return Urql.useQuery<HistoricalWeightsQuery, HistoricalWeightsQueryVariables>({ query: HistoricalWeightsDocument, ...options });
};
export const ImmunizationsDocument = `
    query Immunizations($clientId: ID!, $patientId: ID, $ignoreHistorical: Boolean = true, $isLatest: Boolean, $isRabies: Boolean, $limit: Int, $offset: Int) {
  immunizations(
    clientId: $clientId
    patientId: $patientId
    ignoreHistorical: $ignoreHistorical
    isLatest: $isLatest
    isRabies: $isRabies
    limit: $limit
    offset: $offset
  ) {
    id
    dueDate
    date
    administered
    updatedAt
    location {
      id
    }
    isRabies
    name
    patient {
      id
      name
    }
  }
}
    `;

export function useImmunizationsQuery(options: Omit<Urql.UseQueryArgs<ImmunizationsQueryVariables>, 'query'>) {
  return Urql.useQuery<ImmunizationsQuery, ImmunizationsQueryVariables>({ query: ImmunizationsDocument, ...options });
};
export const InvoicePdfDocument = `
    query InvoicePdf($invoiceId: ID!, $coownerClientId: ID) {
  invoicePdf(id: $invoiceId, coownerClientId: $coownerClientId)
}
    `;

export function useInvoicePdfQuery(options: Omit<Urql.UseQueryArgs<InvoicePdfQueryVariables>, 'query'>) {
  return Urql.useQuery<InvoicePdfQuery, InvoicePdfQueryVariables>({ query: InvoicePdfDocument, ...options });
};
export const InvoicesDocument = `
    query Invoices($clientId: ID!, $invoiced: Boolean = true, $posted: Boolean = true, $orderBy: InvoiceSortFieldEnum = DATETIME, $start: DateTime) {
  orders(
    clientId: $clientId
    invoiced: $invoiced
    posted: $posted
    orderBy: $orderBy
    start: $start
  ) {
    id
    datetime
    totalPaid
    patients {
      name
      id
    }
    updatedAt
  }
}
    `;

export function useInvoicesQuery(options: Omit<Urql.UseQueryArgs<InvoicesQueryVariables>, 'query'>) {
  return Urql.useQuery<InvoicesQuery, InvoicesQueryVariables>({ query: InvoicesDocument, ...options });
};
export const LabOrderResultPdfDocument = `
    query LabOrderResultPdf($documentId: ID!) {
  patientDocument(id: $documentId) {
    name
    media
    contentType
  }
}
    `;

export function useLabOrderResultPdfQuery(options: Omit<Urql.UseQueryArgs<LabOrderResultPdfQueryVariables>, 'query'>) {
  return Urql.useQuery<LabOrderResultPdfQuery, LabOrderResultPdfQueryVariables>({ query: LabOrderResultPdfDocument, ...options });
};
export const LocationDocument = `
    query Location($id: ID!) {
  location(id: $id) {
    addressFields {
      city
      line1
      line2
      postalCode
      state
      text
    }
    addressString
    attributes {
      customAttribute {
        id
        name
      }
      value
    }
    displayName
    id
    latitude
    longitude
    phoneNumber
    timezone
    locationHours {
      mondayRanges
      tuesdayRanges
      wednesdayRanges
      thursdayRanges
      fridayRanges
      saturdayRanges
      sundayRanges
    }
  }
}
    `;

export function useLocationQuery(options: Omit<Urql.UseQueryArgs<LocationQueryVariables>, 'query'>) {
  return Urql.useQuery<LocationQuery, LocationQueryVariables>({ query: LocationDocument, ...options });
};
export const LocationsDocument = `
    query Locations {
  locations {
    name
    addressFields {
      city
      line1
      line2
      postalCode
      state
      text
    }
    addressString
    attributes {
      customAttribute {
        id
        name
      }
      value
    }
    displayName
    id
    latitude
    longitude
    phoneNumber
    timezone
    locationHours {
      mondayRanges
      tuesdayRanges
      wednesdayRanges
      thursdayRanges
      fridayRanges
      saturdayRanges
      sundayRanges
    }
  }
}
    `;

export function useLocationsQuery(options?: Omit<Urql.UseQueryArgs<LocationsQueryVariables>, 'query'>) {
  return Urql.useQuery<LocationsQuery, LocationsQueryVariables>({ query: LocationsDocument, ...options });
};
export const MedicationsDocument = `
    query Medications($patientId: ID) {
  medications(patientId: $patientId) {
    id
    name
    daysSupply
    quantity
    quantityUnit
    refillByDate
    refills
    refillsUsed
    start
    status
    stop
    sig
    note
    declined
    dispensed
    dose
    frequency
    prescriber {
      name
    }
    historical
    insertedAt
    updatedAt
    location {
      timezone
    }
  }
}
    `;

export function useMedicationsQuery(options?: Omit<Urql.UseQueryArgs<MedicationsQueryVariables>, 'query'>) {
  return Urql.useQuery<MedicationsQuery, MedicationsQueryVariables>({ query: MedicationsDocument, ...options });
};
export const OrgDocument = `
    query Org {
  org {
    id
    name
  }
}
    `;

export function useOrgQuery(options?: Omit<Urql.UseQueryArgs<OrgQueryVariables>, 'query'>) {
  return Urql.useQuery<OrgQuery, OrgQueryVariables>({ query: OrgDocument, ...options });
};
export const PatientDetailsDocument = `
    query PatientDetails($patientId: ID!) {
  patient(id: $patientId) {
    id
    alerts
    allergies {
      id
      name
    }
    clinicalNotes {
      id
      text
      deleted
    }
  }
}
    `;

export function usePatientDetailsQuery(options: Omit<Urql.UseQueryArgs<PatientDetailsQueryVariables>, 'query'>) {
  return Urql.useQuery<PatientDetailsQuery, PatientDetailsQueryVariables>({ query: PatientDetailsDocument, ...options });
};
export const PatientDocumentsDocument = `
    query patientDocuments($patientId: ID!) {
  patient(id: $patientId) {
    documents {
      id
      insertedAt
      category
      datetime
      name
      media
      uploader {
        id
        name
        providerRole {
          name
        }
      }
      patient {
        name
        id
      }
      location {
        id
      }
    }
    clinicalNotes {
      id
      title
      fieldData
      text
    }
  }
}
    `;

export function usePatientDocumentsQuery(options: Omit<Urql.UseQueryArgs<PatientDocumentsQueryVariables>, 'query'>) {
  return Urql.useQuery<PatientDocumentsQuery, PatientDocumentsQueryVariables>({ query: PatientDocumentsDocument, ...options });
};
export const PreviousAppointmentsDocument = `
    query PreviousAppointments($clientId: ID!, $patientId: ID, $start: DateTime, $end: DateTime, $limit: Int, $offset: Int) {
  appointments(
    clientId: $clientId
    patientId: $patientId
    start: $start
    end: $end
    limit: $limit
    offset: $offset
  ) {
    id
    patient {
      name
      species
    }
    start
    location {
      addressFields {
        city
        state
      }
      name
      timezone
    }
    type {
      name
    }
    encounters {
      id
      sharedAt
      encounterType {
        id
        name
        sections {
          isPrivate
          name
        }
      }
      sectionsJson
    }
  }
}
    `;

export function usePreviousAppointmentsQuery(options: Omit<Urql.UseQueryArgs<PreviousAppointmentsQueryVariables>, 'query'>) {
  return Urql.useQuery<PreviousAppointmentsQuery, PreviousAppointmentsQueryVariables>({ query: PreviousAppointmentsDocument, ...options });
};
export const RelationshipsDocument = `
    query Relationships {
  relationships {
    id
    name
  }
}
    `;

export function useRelationshipsQuery(options?: Omit<Urql.UseQueryArgs<RelationshipsQueryVariables>, 'query'>) {
  return Urql.useQuery<RelationshipsQuery, RelationshipsQueryVariables>({ query: RelationshipsDocument, ...options });
};
export const UserInformationDocument = `
    query UserInformation($id: ID!) {
  client(id: $id) {
    id
    coowners {
      id
      coownerClient {
        id
        familyName
        givenName
        email
      }
      patient {
        id
        name
      }
      email
      phoneNumber
      name
      relationship
      relationshipId
      notes
    }
  }
  relationships {
    id
    name
  }
}
    `;

export function useUserInformationQuery(options: Omit<Urql.UseQueryArgs<UserInformationQueryVariables>, 'query'>) {
  return Urql.useQuery<UserInformationQuery, UserInformationQueryVariables>({ query: UserInformationDocument, ...options });
};
export const VerifyUserAppointmentsDocument = `
    query VerifyUserAppointments($clientId: ID!, $start: DateTime) {
  appointments(clientId: $clientId, start: $start) {
    id
    status
    reason
    type {
      id
      name
    }
    start
    provider {
      id
    }
    note
    location {
      id
      name
      displayName
      addressFields {
        city
        state
        line1
        line2
        postalCode
      }
      phoneNumber
      latitude
      longitude
      timezone
      attributes {
        customAttribute {
          id
          name
        }
        value
      }
    }
    patient {
      id
      name
      species
    }
  }
}
    `;

export function useVerifyUserAppointmentsQuery(options: Omit<Urql.UseQueryArgs<VerifyUserAppointmentsQueryVariables>, 'query'>) {
  return Urql.useQuery<VerifyUserAppointmentsQuery, VerifyUserAppointmentsQueryVariables>({ query: VerifyUserAppointmentsDocument, ...options });
};